/**
 * BrepKernel — the production GeomKernel backed by OpenCascade in a worker.
 *
 * Geometry is EXACT B-rep: sketches become true curves, booleans are
 * analytic, and fillet/chamfer/shell exist. Meshes returned to the app are
 * just display tessellations; the real shapes live in the worker's registry
 * keyed by feature id (`brepId` on results).
 *
 * Division of labor:
 *  - sketch + import-mesh run on the JS kernel (pure 2D / display meshes)
 *  - solid features proxy to the worker
 *  - features whose TARGET is a mesh-only body (imported STL etc.) fall back
 *    to the JS mesh kernel transparently — mixed documents keep working
 *  - if the 50MB WASM fails to load, the whole kernel degrades to JS mesh
 *    mode and says so once
 */

import * as Comlink from 'comlink';

import { JsKernel } from './jsKernel';
import { offsetPlaneMatrix } from './sketch';
import { loadBrepText } from '../storage/assets';
import { expandTextEntities } from '../text/fontCache';
import {
  FeatureError,
  type EvalContext,
  type GeometryResult,
  type GeomKernel,
} from './types';
import type { FeatureId, FeatureNode } from '../timeline/types';
import type { OcctWorkerApi } from '@workers/occt.worker';
import type { SketchInput } from '@workers/occtCore';

const SOLID_TYPES = new Set([
  'extrude', 'revolve', 'sweep', 'loft', 'scale', 'mirror', 'transform',
  'group', 'instance', 'part', 'fillet', 'chamfer', 'shell', 'hole', 'coil',
  'primitive', 'import-step', 'draft', 'thread', 'press-pull',
]);

/** Heap size that triggers a worker restart (Emscripten never shrinks). */
const COMPACT_THRESHOLD_BYTES = 1_200_000_000;
/** How often (executes) to poll the worker heap. */
const COMPACT_CHECK_EVERY = 20;

export class BrepKernel implements GeomKernel {
  private readonly fallback = new JsKernel();
  private worker: Comlink.Remote<OcctWorkerApi> | null = null;
  private rawWorker: Worker | null = null;
  private initPromise: Promise<boolean> | null = null;
  private executesSinceCheck = 0;
  /** 'pending' → 'brep' once WASM is up, or 'mesh' if it failed. */
  mode: 'pending' | 'brep' | 'mesh' = 'pending';
  onReady: ((mode: 'brep' | 'mesh') => void) | null = null;

  private ensureInit(): Promise<boolean> {
    if (!this.initPromise) {
      try {
        this.rawWorker = new Worker(
          new URL('../../workers/occt.worker.ts', import.meta.url),
          { type: 'module' },
        );
        this.worker = Comlink.wrap<OcctWorkerApi>(this.rawWorker);
        this.initPromise = this.worker
          .init()
          .then(() => {
            this.mode = 'brep';
            this.onReady?.('brep');
            return true;
          })
          .catch((err) => {
            console.warn('OCCT init failed — falling back to the mesh kernel', err);
            this.mode = 'mesh';
            this.onReady?.('mesh');
            return false;
          });
      } catch (err) {
        console.warn('OCCT worker unavailable — mesh kernel only', err);
        this.mode = 'mesh';
        this.initPromise = Promise.resolve(false);
        this.onReady?.('mesh');
      }
    }
    return this.initPromise;
  }

  /** Kick off WASM loading early (call at boot). */
  warmup(): void {
    void this.ensureInit();
  }

  /**
   * Resolves once WASM init SETTLES: 'brep' on success, 'mesh' on failure.
   * Routing decisions (exact STEP import vs mesh fallback) must wait for
   * this rather than read `mode` while it is still 'pending'.
   */
  async whenReady(): Promise<'brep' | 'mesh'> {
    return (await this.ensureInit()) ? 'brep' : 'mesh';
  }

  async execute(feature: FeatureNode, ctx: EvalContext): Promise<GeometryResult> {
    if (!SOLID_TYPES.has(feature.type)) {
      return this.fallback.execute(feature, ctx); // sketch, import-mesh
    }
    const ready = await this.ensureInit();
    if (!ready || !this.worker) return this.fallback.execute(feature, ctx);

    // Gather inputs: sketches (entities + matrix) and shape roles.
    const params = feature.params as unknown as Record<string, unknown>;
    const sketches: Record<string, SketchInput> = {};
    const shapes: Record<string, string[]> = {};
    let assets: Record<string, string> | undefined;
    const consumed: FeatureId[] = [];

    const refOf = (v: unknown): { $feature: FeatureId; path?: string } | null =>
      v && typeof v === 'object' && '$feature' in (v as object)
        ? (v as { $feature: FeatureId; path?: string })
        : null;

    const needSketch = (ref: { $feature: FeatureId }): boolean => {
      const input = ctx.inputs.get(ref.$feature);
      if (input?.kind !== 'sketch') return false;
      sketches[ref.$feature] = {
        // Text expands to outline loops HERE (main thread owns the font);
        // the worker never sees a raw text entity.
        entities: [...expandTextEntities(input.entities)],
        matrix: [...input.planeWorld],
      };
      return true;
    };

    /** Resolve a body role; null means mesh-only input → JS fallback. */
    const needShape = (role: string, ref: { $feature: FeatureId }): boolean => {
      const input = ctx.inputs.get(ref.$feature);
      if (input?.kind !== 'body') return false;
      if (!input.brepId) return false; // mesh-only body
      shapes[role] = [...(shapes[role] ?? []), input.brepId];
      return true;
    };

    switch (feature.type) {
      case 'extrude':
      case 'revolve': {
        if (!needSketch(refOf(params.profile)!)) {
          throw new FeatureError(feature.id, `${feature.type} requires a sketch profile`);
        }
        break;
      }
      case 'sweep': {
        if (!needSketch(refOf(params.profile)!) || !needSketch(refOf(params.path)!)) {
          throw new FeatureError(feature.id, 'Sweep requires profile and path sketches');
        }
        break;
      }
      case 'loft': {
        const profiles = (params.profiles as { $feature: FeatureId }[]) ?? [];
        if (profiles.length < 2) {
          throw new FeatureError(feature.id, 'Loft needs at least 2 profile sketches');
        }
        for (const p of profiles) {
          if (!needSketch(p)) {
            throw new FeatureError(feature.id, 'Every loft profile must be a sketch');
          }
        }
        const spine = refOf(params.spine);
        if (spine && !needSketch(spine)) {
          throw new FeatureError(feature.id, 'Loft spine must be a sketch with an open curve');
        }
        break;
      }
      case 'group': {
        const targets = (params.targets as { $feature: FeatureId }[]) ?? [];
        for (const t of targets) {
          if (!needShape('targets', t)) return this.fallback.execute(feature, ctx);
          consumed.push(t.$feature);
        }
        break;
      }
      case 'part': {
        // Component bodies come from the sub-project evaluation (synthetic
        // target refs); mesh-only sources fall back to the JS merge.
        const targets = (params.targets as { $feature: FeatureId }[]) ?? [];
        if (targets.length === 0) {
          throw new FeatureError(feature.id, 'Part has no source bodies');
        }
        for (const t of targets) {
          if (!needShape('targets', t)) return this.fallback.execute(feature, ctx);
        }
        break;
      }
      case 'coil':
      case 'primitive':
      case 'thread':
        break; // standalone solids — only the implicit world below
      case 'import-step': {
        // The file text lives in the assets store (main thread owns Dexie);
        // ship it to the worker as a side-channel input.
        const hash = params.assetHash as string;
        const text = await loadBrepText(hash);
        if (!text) {
          throw new FeatureError(feature.id, 'Imported file is missing from the asset store');
        }
        assets = { [hash]: text };
        break;
      }
      default: {
        const target = refOf(params.target) ?? refOf(params.source);
        if (!target) throw new FeatureError(feature.id, `${feature.type} needs a target body`);
        if (!needShape('target', target)) {
          if (
            feature.type === 'fillet' ||
            feature.type === 'chamfer' ||
            feature.type === 'shell' ||
            feature.type === 'hole' ||
            feature.type === 'draft' ||
            feature.type === 'press-pull'
          ) {
            throw new FeatureError(
              feature.id,
              `${feature.type} needs an exact B-rep body — imported meshes are display-only`,
            );
          }
          return this.fallback.execute(feature, ctx); // mesh body → mesh math
        }
        if (feature.type !== 'instance') consumed.push(target.$feature);
      }
    }

    // Boolean ops implicitly target the visible world (B-rep bodies only).
    const op = params.op as string | undefined;
    if (op && op !== 'new-body') {
      const worldIds: string[] = [];
      for (const [id, body] of ctx.worldBodies) {
        if (body.brepId) {
          worldIds.push(body.brepId);
          consumed.push(id);
        }
      }
      if (worldIds.length === 0) {
        throw new FeatureError(
          feature.id,
          `Nothing to ${op} — no earlier B-rep bodies exist (imported meshes cannot be boolean targets)`,
        );
      }
      shapes.world = worldIds;
    }

    let mesh;
    try {
      mesh = await this.worker.execute(feature.id, feature.type, params, {
        sketches,
        shapes,
        ...(assets ? { assets } : {}),
      });
    } catch (err) {
      throw new FeatureError(
        feature.id,
        err instanceof Error ? err.message : String(err),
      );
    }

    await this.maybeCompact();

    const result: GeometryResult = {
      kind: 'body',
      mesh,
      brepId: feature.id,
      ...(consumed.length > 0 ? { consumed } : {}),
    };

    // Cap planes: extrude keeps sketchable faces (same math as the JS kernel).
    if (feature.type === 'extrude') {
      const profile = refOf(params.profile)!;
      const sk = ctx.inputs.get(profile.$feature);
      if (sk?.kind === 'sketch') {
        const back = (params.distanceBack as number | undefined) ?? 0;
        let wBottom = -back;
        let wTop = params.distance as number;
        if (wTop < wBottom) [wBottom, wTop] = [wTop, wBottom];
        return {
          ...result,
          capPlanes: {
            top: offsetPlaneMatrix(sk.planeWorld, wTop),
            bottom: offsetPlaneMatrix(sk.planeWorld, wBottom),
          },
        };
      }
    }
    return result;
  }

  prune(liveIds: readonly string[]): void {
    if (this.mode === 'brep') void this.worker?.prune([...liveIds]);
  }

  /**
   * Memory reclamation: WASM heaps only grow, so long sessions slowly eat
   * RAM. Past the threshold, snapshot the shape registry as BREP text,
   * restart the worker, and restore — bounded memory for all-day use.
   */
  private async maybeCompact(): Promise<void> {
    if (this.mode !== 'brep' || !this.worker) return;
    if (++this.executesSinceCheck < COMPACT_CHECK_EVERY) return;
    this.executesSinceCheck = 0;
    const bytes = await this.worker.heapBytes().catch(() => 0);
    if (bytes < COMPACT_THRESHOLD_BYTES) return;
    try {
      console.info(`[NovaCAD] OCCT heap at ${(bytes / 1e6).toFixed(0)}MB — restarting worker`);
      const snapshot = await this.worker.serializeRegistry();
      this.rawWorker?.terminate();
      this.worker = null;
      this.rawWorker = null;
      this.initPromise = null;
      this.mode = 'pending';
      const ok = await this.ensureInit();
      if (ok && this.worker) {
        const n = await (this.worker as Comlink.Remote<OcctWorkerApi>).restoreRegistry(snapshot);
        console.info(`[NovaCAD] worker restarted — ${n} shapes restored`);
      }
    } catch (err) {
      console.warn('Worker compaction failed (continuing without)', err);
    }
  }

  /** Exact volume / area / center of mass of a body (mm³, mm², mm). */
  async massProps(
    brepId: string,
  ): Promise<{ volume: number; area: number; centerOfMass: [number, number, number] }> {
    const ready = await this.ensureInit();
    if (!ready || !this.worker) throw new Error('Mass properties need the B-rep kernel');
    return this.worker.massProps(brepId);
  }

  /** Topology census of a body (solid/face/edge counts by kind). */
  async topologySummary(brepId: string): Promise<import('./types').TopologySummary> {
    const ready = await this.ensureInit();
    if (!ready || !this.worker) throw new Error('Topology summary needs the B-rep kernel');
    return this.worker.topologySummary(brepId) as Promise<import('./types').TopologySummary>;
  }

  /** Sampled wall-thickness distribution (DFM probe). */
  async wallThickness(brepId: string, samples = 200): Promise<import('./types').WallThicknessResult> {
    const ready = await this.ensureInit();
    if (!ready || !this.worker) throw new Error('Wall thickness needs the B-rep kernel');
    return this.worker.wallThickness(brepId, samples) as Promise<import('./types').WallThicknessResult>;
  }

  /** Exact plane∩body cut segments, 2D in the section plane's coords. */
  async crossSection(brepId: string, axis: 'X' | 'Y' | 'Z', offset: number): Promise<Float32Array> {
    const ready = await this.ensureInit();
    if (!ready || !this.worker) throw new Error('Cross-section needs the B-rep kernel');
    return this.worker.crossSection(brepId, axis, offset);
  }

  /** Measure the face/edge of a body nearest a world point. */
  async measureNear(
    brepId: string,
    point: [number, number, number],
    want: 'auto' | 'face' | 'edge',
  ): Promise<import('./types').MeasureResult> {
    const ready = await this.ensureInit();
    if (!ready || !this.worker) throw new Error('measure needs the B-rep kernel');
    return this.worker.measureNear(brepId, point, want) as Promise<import('./types').MeasureResult>;
  }

  /** Exact minimum distance (mm) between two bodies, with witness points. */
  async distance(
    brepIdA: string,
    brepIdB: string,
  ): Promise<{ distance: number; pointA: [number, number, number]; pointB: [number, number, number] }> {
    const ready = await this.ensureInit();
    if (!ready || !this.worker) throw new Error('Distance needs the B-rep kernel');
    return this.worker.distance(brepIdA, brepIdB);
  }

  /** Exact overlap volume (mm³) between two bodies; 0 = no interference. */
  async interference(brepIdA: string, brepIdB: string): Promise<number> {
    const ready = await this.ensureInit();
    if (!ready || !this.worker) throw new Error('Interference check needs the B-rep kernel');
    return this.worker.interference(brepIdA, brepIdB);
  }

  async exportStep(brepIds: readonly string[], names?: readonly string[]): Promise<string> {
    const ready = await this.ensureInit();
    if (!ready || !this.worker) throw new Error('B-rep kernel unavailable');
    return this.worker.exportStep([...brepIds], names ? [...names] : undefined);
  }

  /** HLR drawing projection of the given bodies (drawing sheets). */
  async projectView(
    brepIds: readonly string[],
    dir: [number, number, number],
    xdir: [number, number, number],
    section?: { axis: 'X' | 'Y' | 'Z'; offset: number; flip: boolean },
  ): Promise<{ visible: Float32Array; hidden: Float32Array; circles: Float32Array; sectionSegs: Float32Array }> {
    const ready = await this.ensureInit();
    if (!ready || !this.worker) {
      throw new Error('Drawings need the B-rep kernel');
    }
    return this.worker.projectView([...brepIds], dir, xdir, section);
  }
}
